import { Button, type ColumnDef, columnsToExport, exportRowsToXlsx } from '@fiveox/ui'
import { Download } from 'lucide-react'

export interface ExportButtonProps<T> {
  /** What to write — `list.sorted`: what the search and filters left, every
   *  page of it, in the order on screen. */
  rows: T[]
  /** The list's columns. Those with an `exportValue` become the sheet's, under
   *  their own labels — so the file cannot be headed differently from the
   *  table (the kit's `columnsToExport`). */
  columns: ColumnDef<T>[]
  /** Without the extension; today's date is added — `prodesk-invoices-2026-09-26`. */
  filename: string
  label?: string
}

/** Today in the reader's own day, `YYYY-MM-DD`. */
function today(): string {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
}

/**
 * **Export** — the list as an Excel file (C14 §10.6 item 2; interface I-1),
 * over the kit's `exportRowsToXlsx`. For lists of clients and money: clients,
 * cases, invoices, receipts, the statement, leads and reports.
 */
export function ExportButton<T>({ rows, columns, filename, label = 'Export' }: ExportButtonProps<T>) {
  const sheet = columnsToExport(columns)
  const why =
    rows.length === 0
      ? 'Nothing to export — the list is empty or filtered to nothing.'
      : sheet.length === 0
        ? 'This list has no columns to export.'
        : undefined
  // The title is on a wrapper: a disabled kit button takes no pointer events,
  // so a title on the button itself would never say why.
  return (
    <span title={why ?? `Export ${rows.length} ${rows.length === 1 ? 'row' : 'rows'} to Excel`}>
      <Button
        variant="outline"
        size="sm"
        disabled={why !== undefined}
        aria-label={why ? `${label} — ${why}` : undefined}
        onClick={() => exportRowsToXlsx(rows, sheet, `${filename}-${today()}`)}
      >
        <Download size={14} aria-hidden /> {label}
      </Button>
    </span>
  )
}
